// src/components/dashboard/employer/needs/NeedsListView.tsx
// "Rekrytering" — the employer's job-posting list.
//
// Step 1 of the authoring-surface unification (#223): this list and the Moduler
// list now share the same shell (ContentListShell) + row (ContentEntityRow) +
// action cluster (Redigera / Förhandsgranska / Radera). The whole row opens the
// detail page; the icons act on the posting. Preview shows the candidate-facing
// NeedView ("see it as the audience sees it") — the parallel to the module
// "Visa som student".
import React, { useState } from 'react';
import { useEmployer } from '@/contexts/EmployerContext';
import NeedFormModal from '@/components/dashboard/employer/needs/NeedFormModal';
import RelistNeedDialog from '@/components/dashboard/employer/needs/RelistNeedDialog';
import CreateChooserModal from '@/components/dashboard/employer/CreateChooserModal';
import { useLinkLauncher } from '@/contexts/LinkLauncherContext';
import { Plus, Briefcase, MapPin, Clock, Building2, CalendarClock, CalendarCheck, BookOpen, Users, ChevronRight, Lightbulb, ArrowRight, FileText, CheckCircle } from 'lucide-react';
import { ExplainerCard } from '@/components/custom-ui/ExplainerCard';
import type { NeedStatus } from '@/lib/models/need';
import { useToast } from '@/hooks/use-toast';
import { needApi } from '@/lib/api/needApi';
import { buildNeedMenuItems, toRowActions } from './needMenuActions';

import {
    ActionButton,
    EmptyState,
    ErrorState,
    StatusBadge,
    ContentEntityRow,
    ContentListShell,
    DeleteConfirmDialog,
    ConfirmDialog,
    EntityAvatar,
    IslandSlideOver,
    type RowAction,
} from '@/components/custom-ui';

import { NEXILINK_SPACING } from '@/lib/nexilink-card-styles';
import { needToView } from '@/components/needs/NeedView';
import { formatHoursRange, formatWorkLocation, formatShortDate, getNeedStatusLabel, type Need } from '@/lib/models/need';
import { useNavigate, useSearchParams } from 'react-router-dom';

type FilterKey = 'all' | 'active' | 'expired' | 'paused' | 'filled';
const FILTERS: { key: FilterKey; label: string }[] = [
    { key: 'all', label: 'Alla' },
    { key: 'active', label: 'Publicerade' },
    // #281 — expired postings land here (the "review the applications" queue).
    { key: 'expired', label: 'Att granska' },
    { key: 'paused', label: 'Pausade' },
    { key: 'filled', label: 'Tillsatta' },
];

// Compact meta shown under the title inside the shared row (mirrors NeedCard).
const NeedRowMeta: React.FC<{ need: Need }> = ({ need }) => {
    const navigate = useNavigate();
    const data = needToView(need);
    const subtitle = [data.unitLabel, data.organisationName].filter(Boolean).join(' · ');
    const shown = data.competencies.slice(0, 3);
    const extra = data.competencies.length - shown.length;
    return (
        <>
            {subtitle && (
                <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                    <Building2 className="w-3 h-3" /> {subtitle}
                </p>
            )}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-gray-500">
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {data.city || '—'}</span>
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatHoursRange(data.minHours, data.maxHours)}</span>
                <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" /> {formatWorkLocation(data.workLocationOnSite, data.workLocationRemote)}</span>
                {/* #281 — publication date (only once the posting has actually gone live). */}
                {need.published_at && (
                    <span className="flex items-center gap-1"><CalendarCheck className="w-3 h-3" /> Publicerad {formatShortDate(need.published_at)}</span>
                )}
                {data.applicationDeadline && (
                    <span className="flex items-center gap-1"><CalendarClock className="w-3 h-3" /> Sista {formatShortDate(data.applicationDeadline)}</span>
                )}
            </div>
            {/* #281 — application tally. Clickable → the posting's applications
                (the "show a list of all applications" ask). Undecided count is
                called out in amber so "needs handling" reads at a glance. */}
            {(need.application_count ?? 0) > 0 && (
                <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/needs/${need.id}/applications`); }}
                    className="group mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-[#1A4D4F] hover:underline"
                    title="Visa ansökningar"
                >
                    <Users className="w-3.5 h-3.5" />
                    <span>{need.application_count} {need.application_count === 1 ? 'ansökan' : 'ansökningar'}</span>
                    {(need.undecided_count ?? 0) > 0 && (
                        <span className="text-amber-600">· {need.undecided_count} att granska</span>
                    )}
                    <ChevronRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
            )}
            {(shown.length > 0) && (
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    {shown.map((c, i) => (
                        <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-[#A4C3B2]/30 text-[#1A4D4F] font-medium">{c}</span>
                    ))}
                    {extra > 0 && <span className="text-[11px] text-gray-400">+{extra}</span>}
                </div>
            )}
            {/* Prerequisite courses (#40) — reverse of the course "used in" line.
                Same neutral chip; modul shows in the teal book icon. */}
            {(need.required_courses?.length ?? 0) > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Förkunskap</span>
                    {need.required_courses!.map((c) => (
                        <button
                            key={c.id}
                            onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/courses/${c.id}`); }}
                            title={`Öppna förkunskapsmodulen: ${c.name}`}
                            className="group inline-flex items-center gap-1 pl-2 pr-1.5 py-1 rounded-full text-xs font-medium bg-[#1A4D4F]/12 text-[#1A4D4F] border border-[#1A4D4F]/20 hover:bg-[#1A4D4F]/20 hover:border-[#1A4D4F]/40 cursor-pointer transition-colors max-w-[240px]"
                        >
                            <BookOpen className="w-3.5 h-3.5 flex-shrink-0 text-[#1A4D4F]" />
                            <span className="truncate group-hover:underline">{c.name}</span>
                            <ChevronRight className="w-3.5 h-3.5 flex-shrink-0 opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                        </button>
                    ))}
                </div>
            )}
        </>
    );
};

const NeedsListView: React.FC = () => {
    const navigate = useNavigate();
    const {
        activeNeeds,
        isLoadingNeeds,
        needError,
        deleteNeed,
        updateNeedStatus,
        getMyActiveNeeds,
        employerDetails,
    } = useEmployer();

    const [showCreateChooser, setShowCreateChooser] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [editNeed, setEditNeed] = useState<Need | null>(null);
    const [duplicateNeed, setDuplicateNeed] = useState<Need | null>(null);
    const { toast } = useToast();

    // #265 — copy the PUBLIC shareable link (/jobb/:id); parity with the detail page.
    const copyPublicLink = (n: Need) => {
        const url = `${window.location.origin}/jobb/${n.id}`;
        navigator.clipboard.writeText(url).then(
            () => toast({ title: 'Länk kopierad', description: 'Delningslänken till annonsen finns i urklipp.' }),
            () => toast({ title: 'Kunde inte kopiera länken', description: url, variant: 'destructive' }),
        );
    };
    const { stageLink } = useLinkLauncher();
    const [deleteTarget, setDeleteTarget] = useState<Need | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    // Initialise the filter from the URL (?filter=active) so deep-links like the
    // home "Publicerade annonser" stat land on the right tab.
    const [searchParams] = useSearchParams();
    const initialFilter = FILTERS.some((f) => f.key === searchParams.get('filter'))
        ? (searchParams.get('filter') as FilterKey)
        : 'all';
    const [selectedFilter, setSelectedFilter] = useState<FilterKey>(initialFilter);

    // #281 — the "Att granska" tab holds expired postings; badge it with the
    // number that still have undecided applications (the ones needing action).
    const reviewCount = activeNeeds.filter(
        (n) => n.status === 'expired' && (n.undecided_count ?? 0) > 0,
    ).length;

    // #64 — the ⋮ publish/unpublish toggle confirms before flipping.
    const [pendingStatus, setPendingStatus] = useState<{
        need: Need; status: NeedStatus; title: string; description: string; confirmLabel: string;
    } | null>(null);
    const [statusBusy, setStatusBusy] = useState(false);
    // #281 — relist an expired posting straight from the list ⋮.
    const [relistTarget, setRelistTarget] = useState<Need | null>(null);

    const confirmStatusChange = async () => {
        if (!pendingStatus) return;
        setStatusBusy(true);
        try {
            await updateNeedStatus(pendingStatus.need.id, pendingStatus.status);
            setPendingStatus(null);
        } finally {
            setStatusBusy(false);
        }
    };

    const filteredNeeds = activeNeeds.filter((need) =>
        selectedFilter === 'all' ? true : need.status === selectedFilter,
    );

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setIsDeleting(true);
        try {
            await deleteNeed(deleteTarget.id);
            setDeleteTarget(null);
        } finally {
            setIsDeleting(false);
        }
    };

    // Error State — only when the list genuinely failed to load (nothing to
    // show). Transient create/update validation errors are surfaced in the form.
    if (needError && activeNeeds.length === 0) {
        return (
            <div className={NEXILINK_SPACING}>
                <ErrorState message={needError} title="Kunde inte ladda annonser" />
            </div>
        );
    }

    const emptyState = (
        <EmptyState
            icon={Briefcase}
            title="Inga jobbannonser ännu"
            description="Skapa din första jobbannons för att börja matcha med kvalificerade kandidater."
            action={{ label: 'Skapa annons', onClick: () => setShowCreateChooser(true), icon: Plus }}
        />
    );

    const filters = FILTERS.map((f) => (
        <button
            key={f.key}
            onClick={() => setSelectedFilter(f.key)}
            className={`px-5 py-2.5 text-sm font-semibold rounded-xl transition-colors inline-flex items-center gap-2 ${
                selectedFilter === f.key
                    ? 'bg-[#1A4D4F] text-[#f5f5f5]'
                    : 'bg-transparent text-gray-600 hover:bg-gray-50'
            }`}
        >
            {f.label}
            {/* #281 — attention badge on "Att granska" when postings await review. */}
            {f.key === 'expired' && reviewCount > 0 && (
                <span className={`min-w-[1.25rem] px-1.5 h-5 inline-flex items-center justify-center rounded-full text-[11px] font-bold ${
                    selectedFilter === f.key ? 'bg-white/25 text-white' : 'bg-amber-100 text-amber-700'
                }`}>
                    {reviewCount}
                </span>
            )}
        </button>
    ));

    const action = (
        <ActionButton icon={Plus} onClick={() => setShowCreateChooser(true)}>
            Skapa annons
        </ActionButton>
    );

    return (
        <>
            <div className={NEXILINK_SPACING}>
            <ExplainerCard id="employer-needs" icon={Lightbulb} tone="amber" title="Så funkar rekrytering">
                <p className="text-sm text-gray-600 mt-0.5 mb-3">
                    Skapa annonser, ta emot ansökningar och anställ — allt på ett ställe.
                </p>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-1.5 text-sm">
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 text-gray-700">
                        <Briefcase className="w-4 h-4 text-[#1A4D4F]" /> 1. Skapa annons
                    </span>
                    <ArrowRight className="w-4 h-4 text-gray-300 hidden sm:block" />
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 text-gray-700">
                        <FileText className="w-4 h-4 text-[#1A4D4F]" /> 2. Ta emot ansökningar
                    </span>
                    <ArrowRight className="w-4 h-4 text-gray-300 hidden sm:block" />
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 text-gray-700">
                        <CheckCircle className="w-4 h-4 text-[#1A4D4F]" /> 3. Granska & anställ
                    </span>
                </div>
            </ExplainerCard>
            <ContentListShell
                filters={filters}
                action={action}
                isLoading={isLoadingNeeds && activeNeeds.length === 0}
                loadingMessage="Laddar annonser..."
                isEmpty={!isLoadingNeeds && activeNeeds.length === 0}
                emptyState={emptyState}
            >
                {filteredNeeds.length > 0 ? (
                    filteredNeeds.map((need) => {
                        // Menu items come from the SHARED builder so the list ⋮ and the
                        // detail-page ⋮ are guaranteed identical (same items, same order).
                        const actions = toRowActions(buildNeedMenuItems(need, {
                            onEdit: () => setEditNeed(need),
                            onChatWithLink: () => stageLink({
                                reference: { type: 'need', id: need.id },
                                label: need.role,
                                prompt: 'Hjälp mig att jobba vidare med den här annonsen.',
                            }),
                            onPreview: () => navigate(`/dashboard/needs/${need.id}/preview`),
                            onDuplicate: () => setDuplicateNeed(need),
                            onCopyLink: () => copyPublicLink(need),
                            onPublish: () => setPendingStatus({ need, status: 'active', title: 'Publicera annonsen?', description: 'Annonsen blir synlig för kandidater och börjar ta emot ansökningar.', confirmLabel: 'Publicera' }),
                            onUnpublish: () => setPendingStatus({ need, status: 'paused', title: 'Avpublicera annonsen?', description: 'Annonsen döljs för kandidater och slutar ta emot ansökningar. Du kan återpublicera när som helst.', confirmLabel: 'Avpublicera' }),
                            onReopen: () => setPendingStatus({ need, status: 'active', title: 'Återpublicera annonsen?', description: 'Annonsen blir synlig igen och kan ta emot ansökningar.', confirmLabel: 'Återpublicera' }),
                            onRelist: () => setRelistTarget(need),
                            onClose: () => setPendingStatus({ need, status: 'closed', title: 'Stäng annonsen?', description: 'Annonsen avslutas och tas bort från publika listor. Du kan återpublicera senare om du behöver.', confirmLabel: 'Stäng annons' }),
                            onDelete: () => setDeleteTarget(need),
                            onDevExpire: async () => { try { await needApi.devExpireNeed(need.id); await getMyActiveNeeds(); } catch { /* dev-only */ } },
                        }));
                        return (
                            <ContentEntityRow
                                key={need.id}
                                icon={Briefcase}
                                iconTone="brand"
                                leading={
                                    <EntityAvatar
                                        icon={Briefcase}
                                        size="md"
                                        orgName={employerDetails?.organisation.name}
                                        orgLogoUrl={employerDetails?.organisation.logo_url}
                                    />
                                }
                                title={need.role || 'Namnlös roll'}
                                badge={<StatusBadge status={need.status} label={getNeedStatusLabel(need.status)} />}
                                onOpen={() => navigate(`/dashboard/needs/${need.id}`)}
                                actions={actions}
                            >
                                <NeedRowMeta need={need} />
                            </ContentEntityRow>
                        );
                    })
                ) : selectedFilter === 'expired' ? (
                    <div className="p-8 text-center">
                        <p className="text-gray-600">Inga annonser väntar på granskning just nu.</p>
                        <p className="text-sm text-gray-400 mt-1">Annonser hamnar här när sista ansökningsdag passerat.</p>
                    </div>
                ) : (
                    <div className="p-8 text-center">
                        <p className="text-gray-600 mb-4">Inga annonser med valt filter.</p>
                        <ActionButton icon={Plus} onClick={() => setShowCreateChooser(true)}>
                            Skapa annons
                        </ActionButton>
                    </div>
                )}
            </ContentListShell>
            </div>

            {/* Create — fork first (manual form vs Link), same as course creation */}
            {showCreateChooser && (
                <CreateChooserModal
                    title="Skapa annons"
                    question="Hur vill du skapa annonsen?"
                    onClose={() => setShowCreateChooser(false)}
                    manual={{
                        icon: Briefcase,
                        title: 'Skapa annons',
                        description: 'Fyll i annonsen själv — roll, krav och villkor. Du kan alltid låta Link hjälpa till senare.',
                        onClick: () => { setShowCreateChooser(false); setIsCreateModalOpen(true); },
                    }}
                    link={{
                        title: 'Få hjälp av Link',
                        description: 'Beskriv tjänsten i en chatt. Link ställer frågor, föreslår en plan och skapar annonsen åt dig medan du tittar på.',
                        onClick: () => { setShowCreateChooser(false); stageLink({ prompt: 'Hjälp mig att skapa en ny annons.' }); },
                    }}
                />
            )}
            <NeedFormModal
                mode="create"
                open={isCreateModalOpen}
                onOpenChange={setIsCreateModalOpen}
                onSuccess={() => {}}
            />

            {/* Edit */}
            <NeedFormModal
                mode="edit"
                open={!!editNeed}
                existingNeed={editNeed}
                onOpenChange={(open) => { if (!open) setEditNeed(null); }}
                onSuccess={() => setEditNeed(null)}
            />

            {/* Duplicate — parity with the detail page; open the new copy on success. */}
            <NeedFormModal
                mode="duplicate"
                open={!!duplicateNeed}
                existingNeed={duplicateNeed}
                onOpenChange={(open) => { if (!open) setDuplicateNeed(null); }}
                onSuccess={(newNeed?: Need) => { setDuplicateNeed(null); if (newNeed) navigate(`/dashboard/needs/${newNeed.id}`); }}
            />

            {/* Delete */}
            <DeleteConfirmDialog
                open={!!deleteTarget}
                onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
                title="Radera annons"
                entityLabel="annonsen"
                entityName={deleteTarget?.role || ''}
                onConfirm={handleDelete}
                isDeleting={isDeleting}
            />

            {/* #64 — confirm the ⋮ publish/unpublish toggle. */}
            <ConfirmDialog
                open={pendingStatus !== null}
                onOpenChange={(o) => { if (!o) setPendingStatus(null); }}
                title={pendingStatus?.title ?? ''}
                description={pendingStatus?.description}
                confirmLabel={pendingStatus?.confirmLabel ?? 'Bekräfta'}
                isBusy={statusBusy}
                onConfirm={confirmStatusChange}
            />

            {/* #281 — relist an expired posting inline (shared with the detail page). */}
            <RelistNeedDialog
                need={relistTarget}
                open={relistTarget !== null}
                onOpenChange={(o) => { if (!o) setRelistTarget(null); }}
                onDone={() => { setRelistTarget(null); void getMyActiveNeeds(); }}
            />
        </>
    );
};

export default NeedsListView;
